import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { 
  FolderKanban, 
  MessageSquare, 
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Monitor,
  CheckCircle,
  Clock,
  Calendar,
  User,
  Send,
  Bell,
  TrendingUp,
  FileText,
  Users,
  Bot,
  MoreHorizontal,
  Plus
} from 'lucide-react';

const screenshots = [
  {
    id: 'dashboard',
    title: 'Tableau de Bord',
    description: 'Vue d\'ensemble de ton activite : messages, taches, evenements et assistants. Tout au meme endroit.',
    icon: LayoutDashboard,
    color: 'bg-blue-500',
  },
  {
    id: 'pipeline',
    title: 'Pipeline Chantiers',
    description: 'Gerez tes chantiers avec le Kanban : A planifier, Planifie, Demarre, En cours, Termine. Drag & drop intuitif.',
    icon: FolderKanban,
    color: 'bg-orange-500',
  },
  {
    id: 'tchat',
    title: 'Tchat BatiAssist',
    description: 'Communiquez directement avec ton assistant BatiAssist. Il coordonne les agents IA pour executer tes demandes.',
    icon: MessageSquare,
    color: 'bg-green-500',
  },
];

// Maquette Dashboard
function DashboardMockup() {
  return (
    <div className="bg-slate-100 h-full w-full p-3 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
            <span className="text-white text-xs font-bold">B</span>
          </div>
          <span className="text-slate-800 font-semibold text-sm">Espace Client</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
            <Bell className="w-3 h-3 text-slate-500" />
          </div>
          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
            <span className="text-white text-[10px] font-bold">JD</span>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center gap-1 mb-1">
            <MessageSquare className="w-3 h-3 text-green-500" />
            <span className="text-[10px] text-slate-500">Messages</span>
          </div>
          <span className="text-lg font-bold text-slate-800">3</span>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center gap-1 mb-1">
            <CheckCircle className="w-3 h-3 text-blue-500" />
            <span className="text-[10px] text-slate-500">Tâches</span>
          </div>
          <span className="text-lg font-bold text-slate-800">7</span>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center gap-1 mb-1">
            <Calendar className="w-3 h-3 text-purple-500" />
            <span className="text-[10px] text-slate-500">RDV</span>
          </div>
          <span className="text-lg font-bold text-slate-800">2</span>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="w-3 h-3 text-orange-500" />
            <span className="text-[10px] text-slate-500">Devis</span>
          </div>
          <span className="text-lg font-bold text-slate-800">12</span>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-2">
        {/* Messages récents */}
        <div className="bg-white rounded-lg p-2 shadow-sm col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-700">Messages récents</span>
            <span className="text-[10px] text-blue-500">Voir tout</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-1.5 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-medium text-slate-800 truncate">Sarah - Assistante</p>
                <p className="text-[9px] text-slate-500 truncate">J'ai envoyé le devis à M. Dupont</p>
              </div>
              <span className="text-[9px] text-slate-400">10:32</span>
            </div>
            <div className="flex items-center gap-2 p-1.5 bg-slate-50 rounded-lg">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-medium text-slate-800 truncate">Client - Martin</p>
                <p className="text-[9px] text-slate-500 truncate">Merci pour le devis rapide !</p>
              </div>
              <span className="text-[9px] text-slate-400">09:15</span>
            </div>
          </div>
        </div>

        {/* Assistant Status */}
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <span className="text-xs font-semibold text-slate-700 block mb-2">Mon Assistant</span>
          <div className="text-center">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-1">
              <span className="text-white text-sm font-bold">S</span>
            </div>
            <p className="text-[10px] font-medium text-slate-800">Sarah</p>
            <div className="flex items-center justify-center gap-1 mt-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span className="text-[9px] text-green-600">En ligne</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Maquette Pipeline Kanban
function PipelineMockup() {
  return (
    <div className="bg-slate-100 h-full w-full p-3 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FolderKanban className="w-4 h-4 text-orange-500" />
          <span className="text-slate-800 font-semibold text-sm">Pipeline Chantiers</span>
        </div>
        <button className="flex items-center gap-1 px-2 py-1 bg-orange-500 text-white rounded-lg text-[10px] font-medium">
          <Plus className="w-3 h-3" />
          Nouveau
        </button>
      </div>

      {/* Kanban Columns */}
      <div className="flex gap-2 h-[calc(100%-40px)] overflow-x-auto">
        {/* À planifier */}
        <div className="flex-shrink-0 w-[140px] bg-slate-200/50 rounded-lg p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold text-slate-600">À planifier</span>
            <span className="w-4 h-4 bg-slate-300 rounded text-[9px] flex items-center justify-center text-slate-600">2</span>
          </div>
          <div className="space-y-2">
            <div className="bg-white rounded-lg p-2 shadow-sm border-l-2 border-slate-400">
              <p className="text-[10px] font-medium text-slate-800 mb-1">Rénovation cuisine</p>
              <p className="text-[9px] text-slate-500 mb-1">M. Dupont</p>
              <div className="flex items-center gap-1">
                <span className="text-[8px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">12 500€</span>
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm border-l-2 border-slate-400">
              <p className="text-[10px] font-medium text-slate-800 mb-1">Extension garage</p>
              <p className="text-[9px] text-slate-500">Mme Martin</p>
            </div>
          </div>
        </div>

        {/* En cours */}
        <div className="flex-shrink-0 w-[140px] bg-blue-50 rounded-lg p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold text-blue-600">En cours</span>
            <span className="w-4 h-4 bg-blue-200 rounded text-[9px] flex items-center justify-center text-blue-600">3</span>
          </div>
          <div className="space-y-2">
            <div className="bg-white rounded-lg p-2 shadow-sm border-l-2 border-blue-500">
              <p className="text-[10px] font-medium text-slate-800 mb-1">Salle de bain</p>
              <p className="text-[9px] text-slate-500 mb-1">M. Bernard</p>
              <div className="flex items-center gap-1">
                <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="w-[60%] h-full bg-blue-500 rounded-full"></div>
                </div>
                <span className="text-[8px] text-slate-500">60%</span>
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm border-l-2 border-blue-500">
              <p className="text-[10px] font-medium text-slate-800 mb-1">Terrasse bois</p>
              <p className="text-[9px] text-slate-500">M. Petit</p>
            </div>
          </div>
        </div>

        {/* Terminé */}
        <div className="flex-shrink-0 w-[140px] bg-green-50 rounded-lg p-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold text-green-600">Terminé</span>
            <span className="w-4 h-4 bg-green-200 rounded text-[9px] flex items-center justify-center text-green-600">4</span>
          </div>
          <div className="space-y-2">
            <div className="bg-white rounded-lg p-2 shadow-sm border-l-2 border-green-500 opacity-80">
              <p className="text-[10px] font-medium text-slate-800 mb-1">Peinture salon</p>
              <p className="text-[9px] text-slate-500 mb-1">Mme Durand</p>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span className="text-[8px] text-green-600">Payé</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Maquette Chat
function ChatMockup() {
  return (
    <div className="bg-white h-full w-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 p-2 flex items-center gap-2">
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
          <span className="text-white text-sm font-bold">S</span>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-800">Sarah - Mon Assistante</p>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
            <span className="text-[10px] text-green-600">En ligne</span>
          </div>
        </div>
        <MoreHorizontal className="w-4 h-4 text-slate-400" />
      </div>

      {/* Messages */}
      <div className="flex-1 bg-slate-50 p-3 space-y-3 overflow-y-auto">
        {/* Date */}
        <div className="text-center">
          <span className="text-[9px] bg-white px-2 py-0.5 rounded-full text-slate-400 shadow-sm">Aujourd'hui</span>
        </div>

        {/* Message reçu */}
        <div className="flex gap-2">
          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[9px] font-bold">S</span>
          </div>
          <div className="bg-white rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm max-w-[70%]">
            <p className="text-[11px] text-slate-700">Bonjour ! J'ai préparé le devis pour M. Dupont. Il est prêt à être envoyé. Veux-tu que je le lui envoie maintenant ?</p>
            <span className="text-[9px] text-slate-400 mt-1 block">09:32</span>
          </div>
        </div>

        {/* Message envoyé */}
        <div className="flex justify-end">
          <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm px-3 py-2 max-w-[70%]">
            <p className="text-[11px]">Oui, envoie-le et relance aussi Mme Martin pour son acompte stp</p>
            <div className="flex items-center justify-end gap-1 mt-1">
              <span className="text-[9px] text-blue-100">09:35</span>
              <CheckCircle className="w-3 h-3 text-blue-200" />
            </div>
          </div>
        </div>

        {/* Message reçu */}
        <div className="flex gap-2">
          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[9px] font-bold">S</span>
          </div>
          <div className="bg-white rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm max-w-[70%]">
            <p className="text-[11px] text-slate-700">C'est fait ! ✅ Le devis est parti et j'ai relancé Mme Martin. Je te tiens au courant de sa réponse.</p>
            <span className="text-[9px] text-slate-400 mt-1 block">09:38</span>
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-slate-100 p-2 flex items-center gap-2">
        <div className="flex-1 bg-slate-100 rounded-full px-3 py-1.5 text-[11px] text-slate-400">
          Écris ton message...
        </div>
        <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center">
          <Send className="w-3 h-3 text-white" />
        </div>
      </div>
    </div>
  );
}

export default function ScreenshotsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () => setActiveIndex((prev) => (prev + 1) % screenshots.length);
  const prevSlide = () => setActiveIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);

  // Render the appropriate mockup
  const renderMockup = () => {
    switch (screenshots[activeIndex].id) {
      case 'dashboard':
        return <DashboardMockup />;
      case 'pipeline':
        return <PipelineMockup />;
      case 'tchat':
        return <ChatMockup />;
      default:
        return <DashboardMockup />;
    }
  };

  return (
    <section 
      ref={ref}
      className="py-12 sm:py-16 md:py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden"
      data-testid="screenshots-section"
    >
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-8 sm:mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-full mb-4">
            <Monitor className="w-4 h-4" />
            L'application en action
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
            Decouvrez{' '}
            <span className="text-ba-secondary">ton futur espace</span>
          </h2>
          <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto">
            Une interface simple et intuitive concue pour les artisans du batiment.
          </p>
        </motion.div>

        {/* Feature Tabs */}
        <div className="flex justify-center gap-2 sm:gap-4 mb-6 sm:mb-8">
          {screenshots.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveIndex(index)}
                className={`flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-3 rounded-xl transition-all ${
                  activeIndex === index 
                    ? 'bg-white text-slate-900' 
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">{item.title}</span>
              </button>
            );
          })}
        </div>

        {/* Screenshot Display */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          className="relative"
        >
          <div className="bg-slate-800/50 rounded-2xl sm:rounded-3xl p-3 sm:p-6 md:p-8 border border-slate-700">
            <div className="grid lg:grid-cols-5 gap-4 sm:gap-8 items-center">
              {/* Screenshot Preview */}
              <div className="lg:col-span-3 relative order-2 lg:order-1">
                <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-xl sm:rounded-2xl overflow-hidden border border-slate-600 shadow-2xl">
                  {/* Browser Chrome */}
                  <div className="bg-slate-700 h-6 sm:h-8 flex items-center px-2 sm:px-3 gap-1.5 sm:gap-2">
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
                    <div className="flex-1 mx-2 sm:mx-4 bg-slate-600 rounded h-3 sm:h-4 flex items-center px-2">
                      <span className="text-[8px] sm:text-[10px] text-slate-400">batiassist.com/espace-client</span>
                    </div>
                  </div>
                  
                  {/* App Preview - Real Mockup */}
                  <div className="aspect-[16/10] overflow-hidden">
                    {renderMockup()}
                  </div>
                </div>
                
                {/* Navigation Arrows */}
                <button 
                  onClick={prevSlide}
                  className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button 
                  onClick={nextSlide}
                  className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>

              {/* Description */}
              <div className="lg:col-span-2 order-1 lg:order-2 text-center lg:text-left">
                <div className={`w-12 h-12 sm:w-14 sm:h-14 ${screenshots[activeIndex].color} rounded-2xl flex items-center justify-center mb-3 sm:mb-4 mx-auto lg:mx-0`}>
                  {(() => {
                    const Icon = screenshots[activeIndex].icon;
                    return <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />;
                  })()}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">
                  {screenshots[activeIndex].title}
                </h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                  {screenshots[activeIndex].description}
                </p>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-4 sm:mt-6">
            {screenshots.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  activeIndex === index ? 'bg-ba-secondary w-6' : 'bg-slate-600'
                }`}
              />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
